import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import { updateMe } from '@/entities/user';
import { useAuth } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { isLocale, labelOf, useI18n, type Locale } from '@/shared/i18n';
import { PageHeader } from '@/widgets/page-header';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/shadcn/card';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/form';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';

type NameValues = { name: string };
type PasswordValues = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

export function ProfilePage() {
  const { t, locale, setLocale } = useI18n();
  const nameSchema = useMemo(
    () => z.object({ name: z.string().trim().min(1, t.profile.nameRequired) }),
    [t],
  );
  const passwordSchema = useMemo(
    () =>
      z
        .object({
          current_password: z.string().min(1, t.profile.currentPasswordRequired),
          new_password: z.string().min(8, t.profile.passwordMin),
          confirm_password: z.string().min(1, t.profile.confirmNewRequired),
        })
        .refine((values) => values.new_password === values.confirm_password, {
          message: t.profile.mismatch,
          path: ['confirm_password'],
        }),
    [t],
  );
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  const nameForm = useForm<NameValues>({
    resolver: zodResolver(nameSchema),
    values: { name: user?.name ?? '' },
  });

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const nameMutation = useMutation({
    mutationFn: (values: NameValues) => updateMe({ name: values.name }),
    onSuccess: (next) => {
      updateUser(next);
      toast.success(t.profile.nameUpdated);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : t.common.updateFailed);
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (values: PasswordValues) =>
      updateMe({
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    onSuccess: () => {
      passwordForm.reset();
      toast.success(t.profile.passwordUpdated);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : t.common.updateFailed);
    },
  });

  const localeMutation = useMutation({
    mutationFn: (next: Locale) => updateMe({ locale: next }),
    onSuccess: (next) => {
      updateUser(next);
      if (isLocale(next.locale)) setLocale(next.locale);
      toast.success(t.language.saved);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : t.language.failed);
    },
  });

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t.profile.title} description={t.profile.description} />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-4">
        <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t.profile.account}</CardTitle>
            <CardDescription>{t.profile.accountHint}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <p className="text-sm font-medium">{t.common.email}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{t.common.role}</p>
              <p className="text-sm text-muted-foreground">{labelOf(t.roles, user?.role_key)}</p>
            </div>
            <div className="max-w-md space-y-2">
              <p className="text-sm font-medium">{t.language.label}</p>
              <p className="text-sm text-muted-foreground">{t.language.description}</p>
              <Select
                value={locale}
                disabled={localeMutation.isPending}
                onValueChange={(value) => {
                  if (value === 'en' || value === 'ru') localeMutation.mutate(value);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue>
                    {(value) => (value === 'ru' ? t.language.ru : t.language.en)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t.language.en}</SelectItem>
                  <SelectItem value="ru">{t.language.ru}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Form {...nameForm}>
              <form
                className="flex max-w-md flex-col gap-3"
                onSubmit={nameForm.handleSubmit((values) => nameMutation.mutate(values))}
              >
                <FormField
                  control={nameForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.common.name}</FormLabel>
                      <FormControl>
                        <Input autoComplete="name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-fit" disabled={nameMutation.isPending}>
                  {nameMutation.isPending ? t.common.saving : t.profile.saveName}
                </Button>
              </form>
            </Form>

            <Form {...passwordForm}>
              <form
                className="flex max-w-md flex-col gap-3"
                onSubmit={passwordForm.handleSubmit((values) => passwordMutation.mutate(values))}
              >
                <FormField
                  control={passwordForm.control}
                  name="current_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.profile.currentPassword}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="new_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.profile.newPassword}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.profile.confirmNewPassword}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-fit" disabled={passwordMutation.isPending}>
                  {passwordMutation.isPending ? t.common.updating : t.profile.changePassword}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Button
          variant="outline"
          onClick={() => {
            logout();
            navigate('/login');
          }}
        >
          <LogOut />
          {t.profile.logout}
        </Button>
        </div>
      </ScrollArea>
    </div>
  );
}
